import { useState } from 'react';
import {
  Grid,
  GridCustomCellProps,
  GridColumn,
  GridNoRecords,
} from '@progress/kendo-react-grid';
import { SvgIcon } from "@progress/kendo-react-common";
import { circleIcon } from '@progress/kendo-svg-icons';
import { enMessages } from '../../../../../../messages/en-US';
import { useLocalization } from '@progress/kendo-react-intl';
import { Tooltip } from '@progress/kendo-react-tooltip';
import { formatSmartDate } from '../../../../../Shared/Utilities/DateFormattingUtils';
import GatewayTimelineDialog from '../GatewayTimelineDialog';
import EntityNotesButton from '../../Notes/EntityNotesButton';

interface Props {
  assetData?: any;
}

const ConnectivityCell = (props: GridCustomCellProps) => {
  const localizationService = useLocalization();

  // Get the tooltip text based on connectivity status
  let tooltipText = "";
  if (props.dataItem.connectivity === "Online") {
    tooltipText = localizationService.toLanguageString(
      "custom.online",
      enMessages.custom.online
    );
  } else if (props.dataItem.connectivity === "Offline") {
    tooltipText = localizationService.toLanguageString(
      "custom.offline",
      enMessages.custom.offline
    );
  } else if (props.dataItem.connectivity === "Partial") {
    tooltipText = localizationService.toLanguageString(
      "custom.partialConnected",
      enMessages.custom.partialConnected
    );
  } else {
    tooltipText =
      props.dataItem.connectivity === "Unknown"
        ? localizationService.toLanguageString('custom.unknown', enMessages.custom.unknown)
        : props.dataItem.connectivity;
  }

  return (
    <td>
      <SvgIcon
            icon={circleIcon}
            size="large"
            color={
              props.dataItem.connectivity === "Online"
                ? "#3FA43F"
                : props.dataItem.connectivity === "Offline"
                ? "#FF3630"
                : props.dataItem.connectivity === "Partial"
                ? "#FFBF00"
                : "#85868E"
            }
          />
          <span>{tooltipText}</span>
    </td>
  );
};


const LastActivityCell = (props: GridCustomCellProps) => {
  const localizationService = useLocalization();
  const { displayText: lastSeenText, tooltipText } = formatSmartDate(
    props.dataItem.lastActivity,
    localizationService,
    enMessages
  );

  return (
    <td>
      <Tooltip 
        anchorElement="target" 
        position="bottom"
        parentTitle={true}
        content={() => tooltipText}
      >
        <span style={{ cursor: 'pointer' }} title=" ">
          {lastSeenText}
        </span>
      </Tooltip>
    </td>
  );
};

const createSerialNumberCell = (onSerialNumberClick: (gateway: any) => void) =>
  (props: GridCustomCellProps) => {
    const hasAssignedGateway = !!props.dataItem?.id;
    const serialNumber = props.dataItem?.serialNumber === 'Unknown'
      ? ''
      : (props.dataItem?.serialNumber ?? '');
    const handleClick = () => {
      if (!hasAssignedGateway) return;
      onSerialNumberClick(props.dataItem);
    };

    return (
      <td>
        <span
          role={hasAssignedGateway ? "button" : undefined}
          tabIndex={hasAssignedGateway ? 0 : undefined}
          onClick={hasAssignedGateway ? handleClick : undefined}
          onKeyDown={hasAssignedGateway ? (e) => e.key === 'Enter' && handleClick() : undefined}
          className={hasAssignedGateway
            ? "text-blue-600 hover:text-blue-800 underline cursor-pointer"
            : "text-gray-500 cursor-default"
          }
        >
          {serialNumber}
        </span>
      </td>
    );
  };

const ConnectivityHeaderCell = (props) => {
  const localizationService = useLocalization();
  
  const TooltipContent = () => (
    <div className="p-2">
      <div>{localizationService.toLanguageString('custom.connectivityTooltip', enMessages.custom.connectivityTooltip)}</div>
      <div className="flex items-center gap-2">
        <SvgIcon icon={circleIcon} size="small" color="#3FA43F" />
        <span>{localizationService.toLanguageString("custom.online", enMessages.custom.online)}</span>
      </div>
      <div className="flex items-center gap-2">
        <SvgIcon icon={circleIcon} size="small" color="#FF3630" />
        <span>{localizationService.toLanguageString("custom.offline", enMessages.custom.offline)}</span>
      </div>
    </div>
  );

  return (
    <Tooltip 
      anchorElement="target" 
      position="bottom"
      parentTitle={true}
      content={TooltipContent}
    >
      <span style={{ cursor: 'pointer' }} title=" ">
        {props.title}
      </span>
    </Tooltip>
  );
};

const NotesCell = (props: GridCustomCellProps) => {
  const gateway = props.dataItem;
  if (!gateway?.id) return <td />;
  return (
    <td>
      <EntityNotesButton
        entityType="Device"
        entityId={String(gateway.id)}
        entityName={gateway.name || gateway.serialNumber}
        entitySerial={gateway.serialNumber}
      />
    </td>
  );
};

const GatewayTableComponent: React.FC<Props> = ({ assetData = {} }) => {
  const localizationService = useLocalization();
  const [selectedGateway, setSelectedGateway] = useState<any>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Show all configured gateway slots, even when no device is assigned yet.
  const gateways = assetData?.assetConfigurationDetails?.gateways ?? [];

  const handleSerialNumberClick = (gateway: any) => {
    setSelectedGateway(gateway);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedGateway(null);
  };
 
  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="text-lg font-bold mb-4">{localizationService.toLanguageString('custom.gateway', enMessages.custom.gateway)}</div>
          <Grid
          data={gateways}
          style={{ overflow: 'auto' }}
          scrollable={'scrollable'}
          >
            <GridNoRecords>{localizationService.toLanguageString('custom.noGatewayConfigured', enMessages.custom.noGatewayConfigured)}</GridNoRecords>
            <GridColumn
              field="name"
              title={localizationService.toLanguageString('custom.name', enMessages.custom.name)}
            />
            <GridColumn
              field="serialNumber"
              title={localizationService.toLanguageString('custom.serialNumber', enMessages.custom.serialNumber)}
              cells={{ data: createSerialNumberCell(handleSerialNumberClick) }}
            />
            <GridColumn
              field="connectivity"
              title={localizationService.toLanguageString(
                "custom.connectivity",
                enMessages.custom.connectivity
              )}
              cells={{ data: ConnectivityCell }}
              headerCell={ConnectivityHeaderCell}
            />
            <GridColumn field="lastActivity" title={localizationService.toLanguageString('custom.lastActivity', enMessages.custom.lastActivity)} cell={LastActivityCell} />
            <GridColumn
              title=" "
              width={65}
              cells={{ data: NotesCell }}
            />
          </Grid>

      {isDialogOpen && selectedGateway && (
        <GatewayTimelineDialog
          serialNumber={selectedGateway.serialNumber}
          gatewayId={selectedGateway.id || ''}
          assetName={assetData?.name}
          onClose={handleCloseDialog}
        />
      )}
    </div>
  );
};

export default GatewayTableComponent; 